// Imports => React
import { useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import {
  AcCard,
  AcEmptyBlock,
  AcHeading,
  AcIsSet,
  AcTabContainer,
  AcLoaderInline,
  KEYS,
  THEMES,
  TYPES,
  AcTextInput,
  AcGetHash,
  AcIsString,
  AcIsEmptyString,
} from "@opp/ui-kit";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcRichContent,
  AcButton,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import { AcFormatCurrencyAmount } from "@utils";
import { useFormActions } from "@hooks";
import getSymbolFromCurrency from "currency-symbol-map";
import { AcFormatCurrencySeparator } from "../../utilities";

const _CLASSES = {
  MAIN: "ac-withdraw-balance-modal ac-modal--size-lg",
  CONTENT: "ac-withdraw-balance-modal__content",
};

const AcWithdrawBalanceModal = ({
  store: { ui, profile, balance, movements },
  data,
  i18n,
  t,
}) => {
  const [profileUid, setProfileUid] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const {
    current_profile_balance,
    balance: { profile_balances },
  } = balance;
  const current_balance_is_loading = balance.is_profile_balance_loading;

  const { current_partner_currency } = profile;

  const [fields, setFields] = useState({
    amount: null,
    amount_decimal: null,
    description: "",
  });

  const [errors, setErrors] = useState({
    amount: null,
    amount_decimal: null,
    description: null,
  });

  const { hasErrors, handleInputChange, handleInputValidation } =
    useFormActions({
      t,
      fields,
      setFields,
      errors,
      setErrors,
    });

  const getAmountsFromMaximum = useMemo(() => {
    if (!AcIsSet(current_profile_balance)) {
      return [0, 0];
    }
    const amountWithdrawable =
      current_profile_balance.amount - current_profile_balance.amount_planned;
    const amount = amountWithdrawable.toString().slice(0, -2);
    const amountDecimal = amountWithdrawable.toString().slice(-2);

    return [amount, amountDecimal];
  }, [current_profile_balance]);

  const checkValidWithdrawAmount = useMemo(() => {
    if (!AcIsSet(current_profile_balance)) {
      return false;
    }

    const amountWithdrawable =
      parseInt(current_profile_balance.amount) -
      parseInt(current_profile_balance.amount_planned);
    if (amountWithdrawable <= 0) {
      return false;
    }

    if (parseInt(fields.amount) < 0) {
      return false;
    }

    if (parseInt(fields.amount_decimal) < 0) {
      return false;
    }

    if (parseInt(fields.amount) <= 0 && parseInt(fields.amount_decimal) <= 0) {
      return false;
    }

    if (
      isNaN(parseInt(fields.amount)) &&
      isNaN(parseInt(fields.amount_decimal))
    ) {
      return false;
    }

    return true;
  }, [current_profile_balance, fields.amount, fields.amount_decimal]);

  const checkMaxAmount = useMemo(() => {
    if (!AcIsSet(current_profile_balance)) {
      return false;
    }

    let maxAmount;
    let maxAmountDecimal;
    [maxAmount, maxAmountDecimal] = getAmountsFromMaximum;

    if (parseInt(fields.amount) > parseInt(maxAmount)) {
      return false;
    }

    if (
      parseInt(fields.amount) === parseInt(maxAmount) &&
      parseInt(fields.amount_decimal) > parseInt(maxAmountDecimal)
    ) {
      return false;
    }

    return true;
  }, [
    current_profile_balance,
    fields.amount,
    fields.amount_decimal,
    getAmountsFromMaximum,
  ]);

  useEffect(() => {
    const hash = AcGetHash();
    if (hash.startsWith("prf_") && hash !== profileUid) {
      setProfileUid(hash);
    }
    if (
      !hash.startsWith("prf_") &&
      AcIsSet(profile.profiles) &&
      profile.profiles.length > 0
    ) {
      setProfileUid(profile.profiles[0].uid);
    }
  }, [profile.profiles]);

  useEffect(() => {
    if (
      AcIsSet(profileUid) &&
      AcIsString(profileUid) &&
      profileUid.startsWith("prf_")
    ) {
      balance.get_profile(t, profileUid);
    }
  }, [profileUid]);

  const handleClose = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    ui.reset(KEYS.MODAL, false);
  };

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getCloseButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.LIGHT,
      title: `${t("cancel", "Cancel", { ns: "common" })}`,
      callback: handleClose,
    };
  }, [handleClose]);

  const handleSubmitWithdrawConfirm = () => {
    return new Promise(async (resolve, reject) => {
      let amountDec = fields.amount_decimal
        ? fields.amount_decimal.toString()
        : "0";
      if (amountDec.length === 1) {
        amountDec = amountDec + "0";
      }

      const data = {
        amount1: fields.amount,
        amount2: parseInt(amountDec),
        description: fields.description,
        merchant_profile_uid: profileUid,
      };

      await balance
        .withdraw(t, data)
        .then(() => {
          balance.get(t);
          movements.setPageNumber(1);
          movements.list(t, {
            order: "-date",
            date_start: "01-01-2000",
            perpage: 4,
            force_simple_pagination: true,
          });
          resolve();
          ui.setValue(KEYS.MODAL, KEYS.VISIBLE, false);
        })
        .catch(() => {
          reject();
        });
    });
  };

  const handleSubmitWithdraw = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.persist) {
      event.persist();
    }

    setSubmitClicked(true);

    if (hasErrors) {
      return;
    }

    if (getFeedbackText) {
      return;
    }

    let amountDec = fields.amount_decimal
      ? fields.amount_decimal.toString()
      : "0";
    if (amountDec.length === 1) {
      amountDec = amountDec + "0";
    }

    let amount = fields.amount ? fields.amount.toString() : "0";

    amount = parseInt(amount + amountDec);

    let amountFormatted = AcFormatCurrencyAmount(
      amount,
      current_profile_balance.currency,
      i18n.resolvedLanguage,
      true
    );

    if (isNaN(amount)) {
      amountFormatted = "?";
    }

    ui.setInstance(KEYS.CONFIRM_MODAL, {
      callback: () => {
        ui.setValue(KEYS.CONFIRM_MODAL, KEYS.VISIBLE, false);
      },
      visible: true,
      props: {
        content: `${t("withdraw-balance-confirm", {
          amount: amountFormatted,
          namespace: "dashboard",
        })}`,
        cancel: {
          label: t("cancel", { ns: "common" }),
          callback: () => {
            ui.setValue(KEYS.CONFIRM_MODAL, KEYS.VISIBLE, false);
          },
        },
        confirm: {
          label: t("confirm", { ns: "common" }),
          callback: handleSubmitWithdrawConfirm,
        },
      },
    });
  };

  const getWithdrawButtonOptions = useMemo(() => {
    return {
      type: TYPES.SUBMIT,
      theme: THEMES.OMEGA,
      title: `${t("withdraw", "Withdraw", { ns: "dashboard" })}`,
      callback: handleSubmitWithdraw,
      loading: balance.is_busy,
    };
  }, [fields, errors, hasErrors, balance.is_busy, checkValidWithdrawAmount]);

  const getCurrentBalanceFormatted = useMemo(() => {
    if (current_balance_is_loading) {
      return <AcLoaderInline dense cover loading />;
    }
    if (!AcIsSet(current_profile_balance)) {
      return "";
    }

    const amount =
      current_profile_balance.amount - current_profile_balance.amount_planned;
    return (
      <>
        {AcFormatCurrencyAmount(
          amount,
          current_profile_balance.currency,
          i18n.resolvedLanguage,
          true
        )}
      </>
    );
  }, [
    current_profile_balance,
    current_balance_is_loading,
    i18n.resolvedLanguage,
  ]);

  const getFeedbackText = useMemo(() => {
    if (!AcIsSet(fields.description) || AcIsEmptyString(fields.description)) {
      return t("description-required", "Description is a required field.", {
        ns: "dashboard",
      });
    }

    if (!checkValidWithdrawAmount) {
      return t("withdraw-amount-not-valid", "Withdraw amount not valid.", {
        ns: "dashboard",
      });
    }

    if (!checkMaxAmount) {
      return t(
        "withdraw-amount-too-much",
        "Withdraw amount too high, please check your available balance",
        { ns: "dashboard" }
      );
    }

    return false;
  }, [
    fields.description,
    i18n.resolvedLanguage,
    checkValidWithdrawAmount,
    checkMaxAmount,
  ]);

  const renderProfile = () => {
    if (!AcIsSet(profile) || profile.is_loading || current_balance_is_loading) {
      return <AcLoaderInline loading />;
    }

    return (
      <div className={"ac-profile__column ac-profile__column--wide"}>
        <AcRow>
          <AcColumn>
            <AcCard
              className={
                "h-padding-0 h-margin-bottom-0 ac-withdraw-balance-modal__profile-content-card"
              }
            >
              <div className={"h-margin-bottom-0"}>
                <AcRow className={"h-text-align-center"}>
                  <AcColumn>
                    <AcHeading
                      theme={THEMES.MEDIUM}
                      rank={6}
                      className={"h-margin-bottom-5"}
                    >
                      {t("available-balance", "Available balance", {
                        ns: "dashboard",
                      })}
                    </AcHeading>
                  </AcColumn>
                </AcRow>

                <AcRow className={"h-text-align-center h-margin-bottom-10"}>
                  <AcColumn>
                    <AcHeading rank={2} theme={THEMES.PITCH}>
                      {getCurrentBalanceFormatted}
                    </AcHeading>
                  </AcColumn>
                </AcRow>

                {renderAmountInputs}

                {getFeedbackText && submitClicked && (
                  <AcRow className={"h-text-align-center h-margin-bottom-10"}>
                    <AcColumn>
                      <AcRichContent
                        className={
                          "h-text-align-center h-margin-y-10 h-text--color-error ac-withdraw-balance-modal__feedback-text"
                        }
                        content={`<p>${getFeedbackText}</p>`}
                      />
                    </AcColumn>
                  </AcRow>
                )}

                <AcRow className={"h-margin-top-20"}>
                  <AcColumn>
                    <AcTextInput {...getDescriptionInputOptions} />
                  </AcColumn>
                </AcRow>
              </div>
            </AcCard>
          </AcColumn>
        </AcRow>
      </div>
    );
  };

  const getAmountInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMERIC,
      name: "amount",
      value: fields.amount,
      // todo fix inputs using use-form-actions hook
      // callback: handleInputChange,
      callback: (event, name, value, type, checked) => {
        setFields((prevState) => ({
          ...prevState,
          amount: value,
        }));
      },
      validation: handleInputValidation,
      required: true,
      tabIndex: 0,
      focus: true,
      className: "ac-withdraw-balance-modal__input-amount",
      placeholder: "0",
    };
  }, [fields, fields.amount]);

  const getAmountDecimalInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMERIC,
      name: "amount_decimal",
      value: fields.amount_decimal,
      // todo fix inputs using use-form-actions hook
      // callback: handleInputChange,
      callback: (event, name, value, type, checked) => {
        setFields((prevState) => ({
          ...prevState,
          amount_decimal: value,
        }));
      },
      validation: handleInputValidation,
      required: true,
      tabIndex: 1,
      className: "ac-withdraw-balance-modal__input-amount-decimal",
      placeholder: "00",
    };
  }, [fields, fields.amount_decimal]);

  const getDescriptionInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: `${t("description-max-280", "Description (max. 280 characters)", {
        ns: "common",
      })}`,
      name: "description",
      value: fields.description,
      // todo fix inputs using use-form-actions hook
      // callback: handleInputChange,
      callback: (event, name, value, type, checked) => {
        setFields((prevState) => ({
          ...prevState,
          description: value,
        }));
      },
      validation: handleInputValidation,
      required: true,
      tabIndex: 2,
    };
  }, [fields, fields.description]);

  const renderAmountInputs = useMemo(() => {
    if (!AcIsSet(current_profile_balance)) {
      return "";
    }

    return (
      <>
        <AcRow className={"h-text-align-center"}>
          <AcColumn xss={12} xs={12} md={12} className={"h-margin-y-0"}>
            {/* todo currency*/}
            <span className={"h-text--color-pitch h-text--semibold"}>{`${t(
              "withdraw-amount",
              "Withdraw amount",
              { ns: "common" }
            )}`}</span>
          </AcColumn>
        </AcRow>
        <AcRow className={"h-justify-content-center h-align-items-center"}>
          <span className={"ac-withdraw-balance-modal__currency-symbol"}>
            {getSymbolFromCurrency(current_profile_balance.currency)}
          </span>

          <AcTextInput {...getAmountInputOptions} />

          <span className={"ac-withdraw-balance-modal__currency-separator"}>
            {AcFormatCurrencySeparator(i18n.resolvedLanguage)}
          </span>

          <AcTextInput {...getAmountDecimalInputOptions} />
        </AcRow>
      </>
    );
  }, [
    current_profile_balance,
    fields,
    fields.amount,
    fields.amount_decimal,
    fields.description,
    i18n.resolvedLanguage,
    current_partner_currency,
  ]);

  const handleTabChange = (uid) => {
    if (uid.startsWith("prf_") && uid !== profileUid) {
      setProfileUid(uid);
    }
  };

  const renderProfiles = useMemo(() => {
    if (!AcIsSet(profile) || profile.is_loading) {
      return <></>;
    }
    if (!AcIsSet(profile.profiles) || profile.profiles.length === 0) {
      return (
        <AcEmptyBlock
          message={t("no-profiles-found", "No profiles found", {
            ns: "common",
          })}
        />
      );
    }

    const collection = profile.profiles.filter(
      (e) => e.status !== "terminated"
    );
    profile_balances.forEach(({ uid, amount, amount_planned }) => {
      const matchedProfile = collection.find((item) => {
        return item.uid === uid;
      });
      // no profile = removed profile with terminated state
      if (!matchedProfile) return;
      matchedProfile.amount = amount - amount_planned;
    });
    const profileWithBankAmount = collection.findIndex((profile) => {
      return profile.amount;
    });
    if (profileWithBankAmount !== -1) {
      collection.unshift(...collection.splice(profileWithBankAmount, 1));
    }
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const profile = collection[n];

      const object = {
        id: profile.uid,
        label: `${profile.name}${
          profile.po_number ? ` - ${profile.po_number}` : ''
        }(${AcFormatCurrencyAmount(
          profile.amount,
          current_partner_currency,
          i18n.resolvedLanguage,
          true
        )})`,
        disabled: false,
        component: renderProfile(),
        allowed: null,
      };

      result.push(object);
    }

    result.unshift({ label: "", id: "EMPTY_OPTION" });
    return (
      <>
        <AcCard
          className={"h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"}
          theme={THEMES.TRANSPARENT}
        >
          <AcContainer className={"h-margin-top-20"}>
            <AcRow>
              <AcColumn className={"h-padding-x-0"}>
                <AcTabContainer
                  disableIndicator={true}
                  collection={result}
                  callback={handleTabChange}
                  selectForMobile={true}
                  onlyDropdown
                  center
                  placeholder={`${t("paymentLinks:chooseProfile")}`}
                />
              </AcColumn>
            </AcRow>
          </AcContainer>
        </AcCard>
      </>
    );
  }, [
    profile,
    profile.profiles,
    current_balance_is_loading,
    current_profile_balance,
    submitClicked,
    getFeedbackText,
    checkValidWithdrawAmount,
  ]);

  const getIntroText = useMemo(() => {
    if (!AcIsSet(profile) || profile.is_loading) {
      return <AcLoaderInline loading />;
    }
    if (!AcIsSet(profile.profiles) || profile.profiles.length === 0) {
      return t("no-profiles-found", "No profiles found", { ns: "common" });
    }

    if (profile.profiles.length === 1) {
      return t("withdraw-balance-intro-one-profile", { ns: "dashboard" });
    }

    return t("withdraw-balance-intro", { ns: "dashboard" });
  }, [profile, profile.profiles, t]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <AcContainer fluid>
          <AcRow>
            <AcColumn>
              <AcRichContent
                content={`<p>${getIntroText}</p>`}
                className={"h-text--color-medium"}
              />
            </AcColumn>
          </AcRow>

          {renderProfiles}

          <AcRow className={"h-margin-top-20"}>
            <AcColumn className={"h-text--align-left h-flex-v-align-center"}>
              <AcButton {...getCloseButtonOptions}>
                <span>{t("cancel", "Cancel", { ns: "common" })}</span>
              </AcButton>
            </AcColumn>

            <AcColumn className={"h-text--align-right"}>
              <AcButton {...getWithdrawButtonOptions}>
                <span>{t("withdraw", "Withdraw", { ns: "dashboard" })}</span>
              </AcButton>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(["dashboard", "common", "store"])(
  withStore(observer(AcWithdrawBalanceModal))
);
